import { Navigate, useLocation, useSearchParams } from 'react-router';
import { PlanPage } from './features/plan/PlanPage';
import { createBrowserRouter, Link } from 'react-router';
import { EmptyState, buttonClassName } from './components';
import { AuthCallback } from './features/auth/AuthCallback';
import { RequireAuth } from './features/auth/RequireAuth';
import { SignIn } from './features/auth/SignIn';
import { ForgotPassword } from './features/auth/ForgotPassword';
import { ResetPassword } from './features/auth/ResetPassword';
import { AccountSecurityPage } from './features/account/AccountSecurityPage';
import { RequestDetailPage } from './features/requests/RequestDetailPage';
import { ThreadsLayout } from './features/threads/ThreadsLayout';
import { NewChatPane } from './features/threads/NewChatPane';
import { ThreadPane } from './features/threads/ThreadPane';
import { BacklogPage } from './features/backlog/BacklogPage';
import { CompletedPage } from './features/completed/CompletedPage';
import { AuditTrailPage } from './features/activity/AuditTrailPage';
import { ProjectsPage } from './features/projects/ProjectsPage';
import { MonthEndPage } from './features/month-end/MonthEndPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { RequireCapability } from './features/workspace/RequireCapability';
import { AppShell } from './features/shell/AppShell';

function NotFound() {
  return (
    <main className="flex min-h-[100dvh] items-center justify-center bg-surface px-4">
      <EmptyState
        title="Page not found"
        description="That link does not lead anywhere in Rundown."
        action={
          <Link to="/" className={buttonClassName({ variant: 'secondary', size: 'sm' })}>
            Back to requests
          </Link>
        }
      />
    </main>
  );
}

/**
 * Routes are data, not JSX trees: `createBrowserRouter` is what unlocks the
 * router's data APIs (loaders, error boundaries, deferred navigation) if we ever
 * want them. We do not use loaders today — TanStack Query owns fetching, and
 * splitting that ownership between two caches is how a codebase ends up with two
 * answers for the same question.
 *
 * The guard wraps the layout rather than each page, so there is exactly one place
 * that decides "signed in or not", and one socket connection underneath it.
 */

/**
 * Send an old planning link to the screen that replaced it, keeping whatever
 * it was pointing AT. `?sprint=` selects the same sprint on Plan and the hash
 * still finds its change-of-plan row once the drawer renders.
 */
function RedirectToPlan({ view }: { readonly view?: 'delivery' }) {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const next = new URLSearchParams(params);
  if (view !== undefined) next.set('view', view);
  const query = next.toString();
  return <Navigate replace to={`/plan${query ? `?${query}` : ''}${hash}`} />;
}

export const router = createBrowserRouter([
  { path: '/signin', element: <SignIn /> },
  // Outside RequireAuth, necessarily: the whole point of these two is that the
  // caller cannot sign in.
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  // Where the OAuth provider returns to. Outside RequireAuth on purpose: the
  // whole point of this route is that the caller is not signed in yet.
  { path: '/auth/callback', element: <AuthCallback /> },
  {
    // Pathless layout route: children resolve against '/'.
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      // Home is a chat workspace: the thread list beside whichever thread is
      // open, or the box for a new one. See ThreadsLayout.
      {
        element: <ThreadsLayout />,
        children: [
          { index: true, element: <NewChatPane /> },
          { path: 'new', element: <NewChatPane /> },
          { path: 't/:threadId', element: <ThreadPane /> },
        ],
      },
      { path: 'requests/:id', element: <RequestDetailPage /> },
      // Per-user, not per-workspace: this is your own account, distinct from
      // /settings which administers the workspace and needs workspace.admin.
      { path: 'account/security', element: <AccountSecurityPage /> },
      // The backlog is the one planning surface both sides share: the client
      // ranks it, Noorix reads that ranking. No capability guard on the screen
      // itself — the drag is gated server-side on `backlog.rank`, and a
      // read-only backlog is meaningful to everyone in the workspace.
      { path: 'backlog', element: <BacklogPage /> },
      // Delivery reads, open to the whole workspace: each one enforces the
      // client/internal split per row in its own query, so there is nothing a
      // route guard would add beyond a second, looser copy of that rule.
      { path: 'completed', element: <CompletedPage /> },
      /**
       * The audit trail. `internal.read`, not `workspace.admin`: the record is
       * how Noorix answers "what happened to this", which is everyone on the
       * team's question, and the endpoint behind it already returns each caller
       * only the half of the history they may see. Admin-gating it would hide
       * the trail from the developers who most often need to read it while
       * hiding nothing from anyone it is actually kept from.
       */
      {
        path: 'audit',
        element: (
          <RequireCapability capability="internal.read">
            <AuditTrailPage />
          </RequireCapability>
        ),
      },
      { path: 'projects', element: <ProjectsPage /> },
      {
        /**
         * Plan — the planning columns and the delivery pipeline, as two
         * layouts over one sprint selection.
         *
         * Ungated, like `backlog` above and for the same reason: both sides
         * need the same projection in front of them to have a conversation
         * about it. What differs by role is never which sprints come back but
         * which FIELDS are on them — the per-person breakdown is absent from a
         * client's response, enforced in backlog.service.ts — and which
         * actions the screen offers, which it reads from capabilities.
         */
        path: 'plan',
        element: <PlanPage />,
      },
      /**
       * The three routes Plan replaced.
       *
       * Kept as redirects rather than deleted: `/sprints?sprint=…#plan-change-…`
       * is linked from the review queue and from notification emails already
       * sent, and a dead link there is a dead end at exactly the moment
       * somebody is trying to answer a question about scope.
       */
      { path: 'planning', element: <RedirectToPlan /> },
      { path: 'sprints', element: <RedirectToPlan /> },
      { path: 'board', element: <RedirectToPlan view="delivery" /> },
      {
        path: 'month-end',
        element: (
          <RequireCapability
            capability="billing.read"
            fallback={
              <EmptyState
                title="Not available"
                description="Month end is restricted to admins and project leads."
              />
            }
          >
            <MonthEndPage />
          </RequireCapability>
        ),
      },
      {
        path: 'settings',
        element: (
          <RequireCapability
            capability="workspace.admin"
            fallback={
              <EmptyState
                title="Not available"
                description="This screen belongs to the delivery side of Rundown."
              />
            }
          >
            <SettingsPage />
          </RequireCapability>
        ),
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);
